import type { ReactElement, ReactNode } from 'react';
import type { DragEndEvent } from '@dnd-kit/core';
import {
    closestCenter,
    DndContext,
    PointerSensor,
    useSensor,
    useSensors
} from '@dnd-kit/core';
import {
    SortableContext,
    verticalListSortingStrategy
} from '@dnd-kit/sortable';

import { SortableRow } from '@/components/SortableRow';

interface SortableListProps<T extends { id: string }> {
    items: T[];
    onReorder: (orderedIds: string[]) => void;
    renderItem: (item: T) => ReactNode;
    testId?: string;
}

export function SortableList<T extends { id: string }>({
    items,
    onReorder,
    renderItem,
    testId
}: SortableListProps<T>): ReactElement {
    const sensors = useSensors(
        useSensor(PointerSensor, { activationConstraint: { distance: 5 } })
    );

    const handleDragEnd = (event: DragEndEvent): void => {
        const { active, over } = event;
        if (!over || active.id === over.id) {
            return;
        }

        const oldIndex = items.findIndex((i) => i.id === active.id);
        const newIndex = items.findIndex((i) => i.id === over.id);
        if (oldIndex === -1 || newIndex === -1) {
            return;
        }

        const reordered = [...items];
        const [moved] = reordered.splice(oldIndex, 1);
        reordered.splice(newIndex, 0, moved);
        onReorder(reordered.map((i) => i.id));
    };

    return (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext items={items.map((i) => i.id)} strategy={verticalListSortingStrategy}>
                <div data-testid={testId}>
                    {items.map((item) => (
                        <SortableRow key={item.id} id={item.id}>
                            {renderItem(item)}
                        </SortableRow>
                    ))}
                </div>
            </SortableContext>
        </DndContext>
    );
}
